import React, { FC } from 'react'
import { useForm, SubmitErrorHandler } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers'
import { DeprecatedControlledTextInput } from 'transfix-form/components/ControlledTextInput'
import { Box } from 'transfix-ui/core'
import * as yup from 'yup'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { Button, InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui'

const EmailInputFormSchema = yup.object({
  email: yup
    .string()
    .email('Please enter a valid email address.')
    .required('This field is required.'),
})

export type EmailInputFormSchemaType = yup.InferType<typeof EmailInputFormSchema>

export interface IEmailInputFormProps {
  defaultValues?: Partial<EmailInputFormSchemaType>
  error?: string
  isLoading?: boolean
  onSuccess: (data: EmailInputFormSchemaType) => void
}

/**
 * Form used for handling email address only
 */
export const EmailInputForm: FC<IEmailInputFormProps> = ({
  error: errorProp,
  defaultValues: defaultValuesProp = {},
  onSuccess: onSuccessProp,
  isLoading,
}) => {
  const { trackEvent } = useFMSAnalytics()
  const { control, errors, handleSubmit } = useForm({
    mode: 'onSubmit',
    resolver: yupResolver(EmailInputFormSchema),
    shouldFocusError: true,
    defaultValues: {
      ...EmailInputFormSchema.cast({
        ...defaultValuesProp,
      }),
    },
  })

  const isInvalid = !!Object.keys({ ...errors }).length

  const failureHandler: SubmitErrorHandler<EmailInputFormSchemaType> = err => {
    trackEvent('Onboarding', 'ONBOARDING_FAILED_TO_SUBMIT_FORM_ON_SIGNUP_PAGE', { err })
  }

  const submitHandler = handleSubmit(onSuccessProp, failureHandler)

  return (
    <form onSubmit={submitHandler} data-test='email-input-form'>
      <Box mb={2}>
        <DeprecatedControlledTextInput
          control={control}
          name='email'
          defaultValue=''
          TextInputProps={{
            autoFocus: true,
            large: true,
            placeholder: 'you@yourcompany.com',
            label: 'Enter your email',
            dataTest: 'input-email',
            validationMessage: errors.email?.message,
            invalid: !!errors.email?.message,
            clearable: false,
            className: 'fs-mask',
          }}
        />
      </Box>
      {errorProp && (
        <Box mb={2}>
          <InlineMessage message={errorProp} type={INLINE_MESSAGE_VARIANTS.ALERT} fullWidth />
        </Box>
      )}
      <Box mt={1} textAlign='center'>
        By clicking Sign Up as a Carrier, you agree to Transfix&apos;s{' '}
        <a href='https://transfix.io/privacy-policy/' title='Privacy Policy'>
          Privacy Policy
        </a>{' '}
        and{' '}
        <a href='https://transfix.io/terms-of-service/' title='Terms of Service'>
          Terms of Service
        </a>
      </Box>
      <Box mt={4}>
        <Button
          color='primary'
          data-test='get-started-button'
          fullWidth
          disabled={isInvalid || isLoading}
          size='large'
          type='submit'>
          Sign Up as a Carrier
        </Button>
      </Box>
    </form>
  )
}
